import { Zap } from "lucide-react";
import { calcScopeCost } from "../proposalUtils";
import { money } from "@/lib/format";
import { RailCard, RailRow, railInputBase, railLink } from "./RailCard";

export const OTHER_COST_KEYS = ["equipment", "equipment_gm", "travel", "travel_gm", "disposal", "disposal_gm", "drive_time", "drive_time_gm", "drive_time_hours"];

// Job-level costs are stored on the first scope so totals, PDF and WO conversion stay unchanged. Older
// proposals that spread them across scopes get folded into the first one on open.
export const consolidateOtherCosts = (scopes) => {
  if (!scopes || scopes.length < 2) return scopes;
  const rest = scopes.slice(1);
  if (!rest.some((s) => ["equipment", "travel", "disposal", "drive_time", "drive_time_hours"].some((k) => parseFloat(s[k]) > 0))) return scopes;
  const sum = (k) => scopes.reduce((a, s) => a + (parseFloat(s[k]) || 0), 0);
  const gmOf = (k, gmKey, def) => { const src = scopes.find((s) => parseFloat(s[k]) > 0); return src ? (src[gmKey] ?? def) : (scopes[0][gmKey] ?? def); };
  const first = {
    ...scopes[0],
    equipment: sum("equipment"), equipment_gm: gmOf("equipment", "equipment_gm", 15),
    travel: sum("travel"), travel_gm: gmOf("travel", "travel_gm", 15),
    disposal: sum("disposal"), disposal_gm: gmOf("disposal", "disposal_gm", 15),
    drive_time: sum("drive_time"), drive_time_gm: gmOf("drive_time", "drive_time_gm", 0),
    drive_time_hours: sum("drive_time_hours"),
  };
  const cleared = { equipment: 0, travel: 0, disposal: 0, drive_time: 0, drive_time_hours: 0 };
  return [first, ...rest.map((s) => ({ ...s, ...cleared }))];
};

const blank = (v) => (v === 0 || v == null ? "" : v);
const ROWS = [
  { key: "equipment", gm: "equipment_gm", label: "Equipment", defGm: 15 },
  { key: "travel", gm: "travel_gm", label: "Travel", defGm: 15 },
  { key: "disposal", gm: "disposal_gm", label: "Disposal", defGm: 15 },
  { key: "drive_time", gm: "drive_time_gm", label: "Drive Time", defGm: 0 },
];

function CostInputs({ scope, row, update, testId }) {
  return (
    <>
      <input type="number" step="0.01" min="0" value={blank(scope[row.key])} placeholder="0" title={`${row.label} cost`}
        onChange={(e) => update(row.key, e.target.value)} onFocus={(e) => e.target.select()} className={`${railInputBase} w-14 text-right`} data-testid={`input-${testId}`} />
      <span className="relative">
        <input type="number" step="1" min="0" max="99" value={Math.round(parseFloat(scope[row.gm] ?? row.defGm) || 0)} title="GM%"
          onChange={(e) => update(row.gm, e.target.value)} onFocus={(e) => e.target.select()} className={`${railInputBase} w-12 pr-4 text-right`} data-testid={`input-${testId}-gm`} />
        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">%</span>
      </span>
    </>
  );
}

// Equipment · Travel · Disposal · Drive time for the whole proposal (never printed as lines on the customer copy).
export function OtherCostsCard({ scopes, updateScope, onAutoDrive }) {
  const host = scopes?.[0];
  if (!host) {
    return <RailCard title="OTHER COSTS" testId="rail-other-costs"><div className="px-3 py-3 text-xs text-slate-400">Add a scope first.</div></RailCard>;
  }
  const update = (k, v) => updateScope(host.id, k, v);
  const billed = ROWS.reduce((a, r) => a + calcScopeCost(host[r.key], host[r.gm] ?? r.defGm), 0);
  return (
    <RailCard title="OTHER COSTS" testId="rail-other-costs"
      right={<span className="text-xs tabular-nums font-semibold text-slate-900" title="Billed total of equipment, travel, disposal and drive time" data-testid="rail-other-costs-total">{money(billed)}</span>}>
      <div className="flex items-center justify-end gap-1.5 px-3 pt-1 text-[10px] uppercase tracking-wide text-slate-400"><span className="w-14 text-right">Cost</span><span className="w-12 text-right">GM</span><span className="w-14 text-right">Billed</span></div>
      {ROWS.map((r) => (
        <RailRow key={r.key} label={r.label} testId={`other-cost-row-${r.key}`}>
          <CostInputs scope={host} row={r} update={update} testId={r.key.replace("_", "")} />
          <span className="w-14 text-right tabular-nums font-semibold text-slate-900" data-testid={`other-cost-billed-${r.key}`}>{money(calcScopeCost(host[r.key], host[r.gm] ?? r.defGm))}</span>
        </RailRow>
      ))}
      <RailRow label="Drive Hours" hint="Added to total hours; no $ effect" testId="other-cost-row-drive-hours">
        <button type="button" className={`${railLink} inline-flex items-center gap-1`} onClick={() => onAutoDrive(host.id)}
          title="Auto-calculate cost + hours from the jobsite distance" data-testid="btn-auto-drivetime"><Zap className="h-3 w-3" /> Auto</button>
        <input type="number" step="0.25" min="0" value={blank(host.drive_time_hours)} placeholder="0" title="Drive-time hours (round trip)"
          onChange={(e) => update("drive_time_hours", e.target.value)} onFocus={(e) => e.target.select()} className={`${railInputBase} w-14 text-right`} data-testid="input-drivetime-hours" />
        <span className="w-14 text-right text-xs text-slate-400">h</span>
      </RailRow>
    </RailCard>
  );
}
